<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxDashboardWidgetFilters from './ConnxDashboardWidgetFilters';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import ConnxDashboardHeader from './ConnxDashboardHeader.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxModal from '@/components/Molecules/ConnxModal.vue';
import ConnxDashboardGrid from './ConnxDashboardGrid.vue';

const widgetSettingsID = ref<string | null>(null);
const dashboardHeader = ref();
const loading = ref(true);
const editing = ref(false);
const settingsModalRef = ref();
const savingWidgetSettings = ref(false);
const mousePosition = ref({ x: -1, y: -1 });

const widgetRefs = ref(new Map());
const dashboardStore = useDashboardStore();
const widgetSettingsRef = ref();
const helpWidgetID = ref<string | null>(null);

const helpFieldText = computed(() => {
  if (helpWidgetID.value) {
    const w = dashboardStore.getDashboardWidgetById(helpWidgetID.value);
    if ((w && w.defaultJsonProperties?.helpFieldText) || w?.helpFieldText) {
      return w.defaultJsonProperties?.helpFieldText ?? w.helpFieldText;
    }
  }
  return null;
});

const syncMousePosition = (event: MouseEvent) => {
  mousePosition.value.x = event.clientX;
  mousePosition.value.y = event.clientY;
};

const activeDashboardWidgets = computed({
  get() {
    return dashboardStore.activeDashboardTab?.dashboardTabWidgets?.filter((w) => !w.pendingRemoval) ?? [];
  },
  set(newWidgets) {
    if (dashboardStore.activeDashboardTab) {
      dashboardStore.activeDashboardTab.dashboardTabWidgets = newWidgets;
    }
  },
});

const reRenderWidgets = () => {
  const widgets = activeDashboardWidgets.value ?? [];
  widgets.forEach((w) => reRenderWidget(w.defaultJsonProperties.i));
};

const reRenderWidget = (id: string) => {
  if (widgetRefs.value.has(id)) {
    setTimeout(() => {
      widgetRefs.value.get(id).reRender();
    }, 250);
  }
};

const toggleEdit = () => {
  editing.value = !editing.value;
  dashboardStore.widgetFilter = '';
  if (editing.value) {
    document.addEventListener('dragover', syncMousePosition);
  } else {
    document.removeEventListener('dragover', syncMousePosition);
  }
  reRenderWidgets();
};

const addNewDashboardTab = async () => {
  await dashboardStore.addNewDashboardTab();
  toggleEdit();
  await nextTick();
  scrollToActiveDashboardTab();

  reRenderWidgets();
};

const scrollToActiveDashboardTab = () => {
  dashboardHeader.value?.scrollToActiveDashboardTab();
};

const saveWidgetSettings = async () => {
  if (widgetSettingsID.value) {
    savingWidgetSettings.value = true;
    await dashboardStore.updateDashboardTabWidgetSettings(widgetSettingsID.value);
    savingWidgetSettings.value = false;
    widgetSettingsID.value = null;
  }
};

// Cancel button: discard draft changes and close modal
const onSettingFilterModalCancel = async () => {
  if (widgetSettingsRef.value) {
    await widgetSettingsRef.value.resetWidget();
  }
  widgetSettingsID.value = null;
};

const onOpenSettingsModal = async (id: string) => {
  widgetSettingsID.value = id;
  await nextTick();
  if (settingsModalRef.value?.focus === 'function') {
    settingsModalRef.value?.focus();
  }
};

const onFiltersToggleEdit = () => {
  editing.value = !editing.value;
  if (editing.value && widgetSettingsID.value) {
    settingsModalRef.value?.scrollToTop();
  }
};

const onHelpModalClose = async () => {
  helpWidgetID.value = null;
};

onMounted(async () => {
  await dashboardStore.init();
  loading.value = false;
  await nextTick();
  scrollToActiveDashboardTab();
});

onBeforeUnmount(() => {
  document.removeEventListener('dragover', syncMousePosition);
});
</script>

<template>
  <div v-if="dashboardStore.errorLoading" class="tw-flex tw-flex-col tw-w-full tw-min-h-content">
    <div class="tw-my-auto tw-flex tw-flex-col tw-items-center tw-justify-center">
      <div class="tw-my-auto tw-flex tw-flex-col tw-items-center tw-justify-center">
        <connx-icon name="fal fa-wifi-slash" class="tw-w-20 tw-h-20 tw-text-connx-grey-tint-40" />
        <h3 class="tw-my-10 ErrorMsg">Network error — unable to load dashboards</h3>
        <div>
          <ConnxButton
            button-style="brand-primary"
            icon-position="before"
            icon="fal fa-heartbeat"
            link="frmAdminSystemStatus.aspx"
            >System Status</ConnxButton
          >
        </div>
      </div>
    </div>
  </div>

  <div v-else class="tw-flex tw-flex-col tw-min-h-content">
    <connx-dashboard-header
      :ref="dashboardHeader"
      @disable-edit="editing = false"
      @toggle-edit="toggleEdit"
      @add-new-dashboard="addNewDashboardTab"
      :editing="editing"
    ></connx-dashboard-header>

    <connx-dashboard-grid
      :editing="editing"
      :loading="loading"
      :mouse-position="mousePosition"
      :widget-settings-id="widgetSettingsID"
      @add-new-dashboard-tab="addNewDashboardTab"
      @open-settings-modal="onOpenSettingsModal"
      @open-help-modal="(helpID) => (helpWidgetID = helpID)"
    />
  </div>

  <connx-modal
    scrollable
    ref="settingsModalRef"
    type="side"
    :title="editing ? 'Settings' : 'Filters'"
    :open="widgetSettingsID !== null"
    @close="onSettingFilterModalCancel"
  >
    <connx-dashboard-widget-filters
      v-if="widgetSettingsID"
      ref="widgetSettingsRef"
      :editing="editing"
      :widget-id="widgetSettingsID"
      @toggle-edit="onFiltersToggleEdit"
    />
    <template #footer>
      <div class="tw-flex tw-items-center tw-justify-between">
        <connx-button button-style="secondary" @click="onSettingFilterModalCancel">Cancel</connx-button>

        <div class="tw-flex tw-items-center tw-justify-end tw-gap-x-4">
          <connx-button
            button-style="success"
            :disabled="savingWidgetSettings || (widgetSettingsRef && widgetSettingsRef?.hasErrors)"
            icon-class="tw-animate-spin"
            :icon="savingWidgetSettings ? 'fad fa-spinner-third' : null"
            @click="saveWidgetSettings"
          >
            <span v-if="savingWidgetSettings">Saving...</span>
            <span v-else>Save Changes</span>
          </connx-button>
        </div>
      </div>
    </template>
  </connx-modal>

  <connx-modal scrollable type="side" title="Help" :open="helpWidgetID !== null" @close="onHelpModalClose">
    <div v-if="helpFieldText" v-html="helpFieldText"> </div>
    <template #footer>
      <div class="tw-flex tw-items-center tw-justify-center">
        <connx-button button-style="secondary" @click="onHelpModalClose">Close</connx-button>
      </div>
    </template>
  </connx-modal>
</template>
